import { AddImageIcon, Typography } from '@execonline-inc/execonline-ui';
import { observer } from 'mobx-react';
import { useState, DragEvent, ChangeEvent, useEffect, useCallback, useContext } from 'react';
import { Maybe, nothing } from 'maybeasy';
import { Link } from '../../../../../../Resource/Types';
import { T, TranslationsContext, translation } from '../../../../../../Translations';
import { UploadImageStore } from '../../../Edit/CurrentCollectionStep/Details/UploadImageStore';
import {
  FileMetaData,
  handleImageChange,
} from '../../../Edit/CurrentCollectionStep/Details/S3ImageUpload/Types';
import Loader from '../../../../../Loader';
import { emptyFragment } from '../../../../../../EmptyFragment';
import { EditLearningCollectionResource, LearningCollectionResource } from '../Types';
import { useLearningCollection } from '../LearningCollectionContext';

type ResourceType = LearningCollectionResource | EditLearningCollectionResource;

interface Props {
  uploadImageStore: UploadImageStore;
  uploadLink: Link;
  links: ReadonlyArray<Link>;
  resourceState: ResourceType;
  setResourceState: (resource: ResourceType | ((prev: ResourceType) => ResourceType)) => void;
}

function LogoUploader({
  uploadImageStore,
  uploadLink,
  links,
  resourceState,
  setResourceState,
}: Props) {
  const ts = useContext(TranslationsContext);
  const { setLogoChange } = useLearningCollection();
  const [isDragOver, setIsDragOver] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<Maybe<Link>>(nothing());

  // Callback for when upload succeeds - called synchronously BEFORE store.ready()
  // This ensures setLogoChange runs before MobX observers unmount the component
  const handleUploadSuccess = useCallback(
    (fileMetaData: FileMetaData) => {
      setLogoChange({ kind: 'upload', data: fileMetaData });
    },
    [setLogoChange],
  );

  // Update resource state links when preview URL is set
  useEffect(() => {
    if (uploadImageStore.state.kind === 'ready' && uploadImageStore.state.fileMetaData.isJust()) {
      // Get the preview URL that was set by handleSuccess
      previewUrl.do((logoLink) => {
        // Use functional updater to avoid stale closure over resourceState
        setResourceState((prev: ResourceType) => {
          // Check if learning-collection-logo link already exists
          const existingLogoLinkIndex = prev.links.findIndex(
            (link: Link) => link.rel === 'learning-collection-logo',
          );

          let updatedLinks: Link[];
          if (existingLogoLinkIndex >= 0) {
            // Update existing logo link
            updatedLinks = prev.links.map((link: Link, index: number) =>
              index === existingLogoLinkIndex ? logoLink : link,
            );
          } else {
            // Add new logo link
            updatedLinks = [...prev.links, logoLink];
          }

          return {
            ...prev,
            links: updatedLinks as typeof prev.links,
          };
        });
      });
    }
  }, [uploadImageStore.state, previewUrl, setResourceState]);

  const handleDragOver = (e: DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      // Create a synthetic event to reuse existing handleImageChange
      const syntheticEvent = {
        target: { files },
      } as ChangeEvent<HTMLInputElement>;

      handleImageChange(
        uploadImageStore,
        uploadLink,
        previewUrl,
        setPreviewUrl,
        handleUploadSuccess,
      )(syntheticEvent);
    }
  };

  const handleFileInputChange = handleImageChange(
    uploadImageStore,
    uploadLink,
    previewUrl,
    setPreviewUrl,
    handleUploadSuccess,
  );

  // Show loading state
  if (uploadImageStore.state.kind === 'loading') {
    return (
      <div className="w-64 h-36 border border-dashed border-border rounded-md bg-muted/10 flex flex-col items-center justify-center">
        <Loader />
        <Typography className="text-muted-foreground mt-2 text-center" variant="bodySmall">
          <T kind="Uploading..." />
        </Typography>
      </div>
    );
  }

  // Show preview if upload succeeded
  const showPreview =
    uploadImageStore.state.kind === 'ready' && uploadImageStore.state.fileMetaData.isJust();

  if (showPreview) {
    return previewUrl
      .map((link) => (
        <div className="w-64">
          <div className="h-36 overflow-hidden flex items-center justify-center border border-border rounded-md">
            <img
              src={link.href}
              alt={translation('Uploaded logo preview')(ts)}
              className="max-h-36 max-w-full object-contain"
            />
          </div>
          <Typography className="text-accent mt-2 text-center" variant="bodySmall">
            <T kind="Upload successful!" />
          </Typography>
        </div>
      ))
      .getOrElse(emptyFragment);
  }

  return (
    <div>
      <input
        type="file"
        accept="image/png, image/jpeg, image/gif"
        id="logo-upload-input"
        className="hidden"
        onChange={handleFileInputChange}
        data-intercom-target="builder-v2-logo-upload-input"
      />
      <label
        htmlFor="logo-upload-input"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`w-64 h-36 border-2 border-dashed rounded-md bg-muted/10 flex flex-col items-center justify-center cursor-pointer transition-all ${
          isDragOver
            ? 'border-primary bg-primary/10'
            : 'border-border hover:border-primary hover:bg-muted/50'
        }`}
        data-intercom-target="builder-v2-logo-upload-area"
      >
        <AddImageIcon size="xl" className="text-muted-foreground" />
        <Typography className="text-muted-foreground w-32 text-center" variant="bodySmall">
          <T kind="Add a collection logo" />
        </Typography>
      </label>
      {uploadImageStore.state.kind === 'error' &&
        uploadImageStore.state.message
          .map((message) => (
            <Typography className="text-destructive mt-2" variant="bodySmall">
              <T kind={message} />
            </Typography>
          ))
          .getOrElse(emptyFragment)}
    </div>
  );
}

export default observer(LogoUploader);
